import { ChevronUp } from '@raydiant/raydial/components/Icons';
import Spinner from '@raydiant/raydial/components/Spinner';
import { useSelector } from 'react-redux';
import { useLibrary } from '../../../../../hooks';
import ApplicationIcon from '../../../../../pages/LibraryPage/v2/components/ApplicationIcon';
import {
  getPresentationData,
  sortContent,
  mergeLibrary,
  DEFAULT_SHARED_LIBRARY_FOLDER_DATA,
} from '../../../../../pages/LibraryPage/utils';
import Text from '@raydiant/raydial/components/Text';
import { memo, useMemo } from 'react';
import { selectSideSortOptions } from '../../../selectors';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import cn from 'classnames';

interface MoveContentBrowserProps {
  scope: string;
  // The folder whose direct children are being browsed. Empty string = root.
  folderId: string;
  // Called when the user clicks a folder to drill into it. The picker's path
  // stack is owned by the parent (MoveContent); this component just emits.
  onNavigate: (folder: { id: string; name: string }) => void;
  disabledFolderIds: string[];
  // True only for the root level of the Shared Libraries scope, so we render
  // the shared-library icon variant instead of the generic folder icon.
  isSharedLibraryRoot?: boolean;
}

const MoveContentTree: React.FC<MoveContentBrowserProps> = ({
  scope,
  folderId,
  onNavigate,
  disabledFolderIds,
  isSharedLibraryRoot = false,
}) => {
  const sortOptions = useSelector(selectSideSortOptions);

  const { data: libraryData, isLoading: libraryLoading } = useLibrary({
    ...DEFAULT_QUERY,
    scope,
    profile_id: '',
    folder_id: folderId || '',
    search: '',
  });

  const mergedLibrary = useMemo(
    () => mergeLibrary(libraryData?.data || []),
    [libraryData],
  );

  // Only folders are navigable destinations; presentations and playlists are
  // intentionally hidden in the destination picker.
  const sortedFolders = useMemo(
    () =>
      sortContent(
        mergedLibrary.filter((content) => content.resourceType === 'folder'),
        sortOptions,
      ),
    [mergedLibrary, sortOptions],
  );

  if (libraryLoading) {
    return (
      <div className="flex justify-center items-center flex-1">
        <Spinner size="large" />
      </div>
    );
  }

  if (sortedFolders.length === 0) {
    return (
      <div className="flex justify-center items-center flex-1 px-4 py-6">
        <Text variant="body" className="text-gray-500">
          This folder is empty
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {sortedFolders.map((folder) => {
        const isDisabled = disabledFolderIds.includes(folder.id);
        return (
          <div
            key={folder.resourceId}
            className={cn(
              'flex cursor-pointer items-center h-12 px-4 gap-2 rounded-2xl hover:bg-blue-50',
              isDisabled && 'opacity-50',
            )}
            onClick={() => onNavigate({ id: folder.id, name: folder.name })}
          >
            <ApplicationIcon
              url={
                isSharedLibraryRoot
                  ? DEFAULT_SHARED_LIBRARY_FOLDER_DATA.applicationIconUrl
                  : getPresentationData(folder).applicationIconUrl
              }
            />
            <div className="flex-1 truncate text-lg font-regular text-black p-2.5">
              {folder.name}
            </div>
            {/* ChevronUp rotated 90° = right chevron (drill-in affordance) */}
            <span
              className="text-gray-400"
              style={{ transform: 'rotate(90deg)', display: 'inline-flex' }}
            >
              <ChevronUp />
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default memo(MoveContentTree);
